import React, { useEffect, useState } from "react";
import { Send, Archive } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg, fmtDateTime } from "@/lib/residentApi";
import { inputCls, GoldButton, Pill, Empty, ErrorLine } from "./ui";
import { useAuth } from "@/lib/auth";
import ResidentPartnerMemos from "@/components/nds/windows/partner/memos/ResidentPartnerMemos";
import MemoReply from "./MemoReply";
import { MemoAssist } from "@/components/memo/MemoAssist";
import { MEMOS_CHANGED } from "@/components/nds/windows/partner/memos/PartnerMemosContent";

const SMS = { sent: "texted", no_gateway: "text not sent · SMS not set up", queued: "text queued", no_phone: "no phone on file", sms_not_configured: "text not sent · SMS not set up", not_requested: "no text", error: "text failed",
  skipped_disabled: "text not sent · SMS not set up", skipped_sms_off: "text not sent · texting off / opted out", skipped_no_phone: "no phone on file", invalid_phone: "text not sent · phone number not valid" };

const PostMemo = ({ rid, sms, onDone }) => {
  const [f, setF] = useState({ subject: "", body: "", send_text: true });
  const [err, setErr] = useState(null);
  const noText = sms && !sms.available;
  const post = async () => {
    setErr(null);
    try { const m = await reApi.post("/memos", { ...f, send_text: f.send_text && !noText, resident_id: rid }); toast.success(`Memo posted · ${noText ? `text not sent · ${sms.reason}` : SMS[m.sms?.status] || m.sms?.status}`); setF((x) => ({ subject: "", body: "", send_text: x.send_text })); onDone(); }
    catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div data-testid="memo-post-form" className="p-3 border border-white/10 rounded-sm space-y-2">
      <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" data-testid="memo-subject" className={inputCls} />
      <textarea rows={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Message to the resident…" data-testid="memo-body" className={inputCls} />
      <MemoAssist value={f.body} onChange={(v) => setF((x) => ({ ...x, body: v }))} id="memo-body" />
      <div className="flex items-center gap-3">
        <label className={`text-[11px] inline-flex items-center gap-1.5 ${noText ? "text-nex-muted/60" : "text-nex-muted"}`}><input type="checkbox" checked={f.send_text && !noText} disabled={noText} onChange={(e) => setF({ ...f, send_text: e.target.checked })} data-testid="memo-send-text" /> Also text the resident</label>
        {noText && <span className="text-[11px] text-amber-300" data-testid="memo-send-text-reason">{sms.reason}</span>}
        <GoldButton onClick={post} disabled={!f.subject.trim() || !f.body.trim()} data-testid="memo-post" className="ml-auto"><Send className="h-3 w-3" /> Post memo</GoldButton>
      </div>
      <ErrorLine testId="memo-post-error">{err}</ErrorLine>
    </div>
  );
};

const Memo = ({ m, manage, info, onDone }) => {
  const [replying, setReplying] = useState(false);
  const [reason, setReason] = useState("");
  const [closing, setClosing] = useState(false);
  const act = async (fn) => { try { await fn(); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <li data-testid={`memo-${m.id}`} className={`p-3 border border-white/5 bg-white/[0.02] rounded-sm space-y-1.5 ${m.status !== "open" ? "opacity-60" : ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-nex-silver font-medium">{m.subject}</span>
        <Pill tone={m.status === "open" ? "gold" : "dim"}>{m.status === "archived" ? "closed" : m.status}</Pill>
        <span className="text-[11px] text-nex-muted">{m.author?.name} · {fmtDateTime(m.created_at)} · {SMS[m.sms?.status] || m.sms?.status || ""}{m.archive_reason ? ` · closed: ${m.archive_reason}` : ""}</span>
      </div>
      <p className="text-sm text-nex-silver/90 whitespace-pre-wrap">{m.body}</p>
      {(m.replies || []).map((r) => (
        <p key={r.id} data-testid={`memo-reply-${r.id}`} className={`pl-3 border-l text-xs ${r.by_kind === "resident" ? "border-emerald-400/40 text-emerald-100/80" : "border-nex-gold/30 text-nex-muted"}`}>
          {r.author?.name} ({r.by_kind}) · {fmtDateTime(r.at)}<span className="block mt-0.5 whitespace-pre-wrap leading-relaxed">{r.body}</span>
        </p>
      ))}
      {manage && m.status === "open" && replying && <MemoReply m={m} info={info} onCancel={() => setReplying(false)} onSent={() => { setReplying(false); onDone(); }} />}
      {manage && m.status === "open" && (
        <div className="flex flex-wrap gap-2 pt-1">
          {!replying && info && <button type="button" onClick={() => setReplying(true)} data-testid={`memo-reply-open-${m.id}`} className="font-mono text-[10px] uppercase text-nex-gold">Reply</button>}
          {!closing && <button type="button" onClick={() => setClosing(true)} data-testid={`memo-close-${m.id}`} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-nex-muted hover:text-nex-silver"><Archive className="h-3 w-3" /> Close memo</button>}
        </div>
      )}
      {manage && m.status === "open" && closing && (
        <div className="flex flex-wrap gap-2" data-testid={`memo-close-form-${m.id}`}>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why it's closed (e.g. talked it through in session)" data-testid={`memo-close-reason-${m.id}`} className={`${inputCls} !py-1 flex-1`} />
          <button type="button" disabled={!reason.trim()} onClick={() => act(() => reApi.patch(`/memos/${m.id}/archive`, { reason }))} data-testid={`memo-close-confirm-${m.id}`} className="font-mono text-[10px] uppercase text-nex-gold disabled:opacity-40">Close it</button>
          <button type="button" onClick={() => setClosing(false)} data-testid={`memo-close-cancel-${m.id}`} className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
        </div>
      )}
    </li>
  );
};

export default function MemoBoardTab({ resident, manage: manageProp, onChanged }) {
  const { user } = useAuth();
  const leaderEdit = user?.role_title === "Resident Leader" && user?.card_access?.cap_re_memos === "edit";
  const manage = manageProp ?? (leaderEdit || (user?.role !== "partner_user" && user?.role !== "resident"));
  const [memos, setMemos] = useState(null);
  const [archived, setArchived] = useState(false);
  const [error, setError] = useState(null);
  const load = () => reApi.get("/memos", { resident_id: resident.id, include_archived: archived }).then((d) => { setMemos(d.memos); window.dispatchEvent(new Event(MEMOS_CHANGED)); }).catch((e) => setError(errMsg(e)));
  const done = () => { load(); onChanged?.(); };
  useEffect(() => { load(); }, [resident.id, archived]); // eslint-disable-line react-hooks/exhaustive-deps
  const [info, setInfo] = useState(null);
  useEffect(() => { if (manage) reApi.get("/memos/compose-info", { resident_id: resident.id }).then(setInfo).catch(() => {}); }, [resident.id, manage]);
  return (
    <div data-testid="memo-board" className="space-y-3">
      {manage ? <PostMemo rid={resident.id} sms={info?.sms} onDone={done} />
        : <p data-testid="memo-readonly-note" className="text-[11px] text-nex-muted">Read-only · you can view memos but not post or reply · memos from the old platform are in Notes → Memo</p>}
      {manage && <ResidentPartnerMemos rid={resident.id} />}
      <ErrorLine testId="memo-error">{error}</ErrorLine>
      <label className="text-[11px] text-nex-muted inline-flex items-center gap-1.5"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} data-testid="memo-show-archived" /> Show closed</label>
      {memos && !memos.length ? <Empty testId="memo-empty">No memos on the board.</Empty> : (
        <ul className="space-y-2" data-testid="memo-list">{(memos || []).map((m) => <Memo key={m.id} m={m} manage={manage} info={info} onDone={done} />)}</ul>
      )}
    </div>
  );
}
